// /forgot-password — request a password-reset code.
//
// Visual + copy from
//   design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md § 5.
// Always-neutral response (no email enumeration): regardless of whether
// the address exists, we transition to the "check your inbox" success
// panel after the API call. The actual enumeration-resistance is
// enforced server-side by Cognito's ForgotPassword API which doesn't
// reveal whether the user exists.

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  LangToggle,
  TextInput,
} from '@gc-coach/design-system';
import { AdminAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

export function ForgotPasswordScreen() {
  const { t, tt, lang, setLang } = useI18n();
  const { status, error, requestPasswordReset, clearError } = useAuth();

  const [email, setEmail] = useState('');

  const submitting = status.kind === 'submitting-reset';
  // status flips to 'forgot-password-sent' on success; we render the success
  // panel when that's the case (the email used is in status.email).
  const sentEmail = status.kind === 'forgot-password-sent' ? status.email : null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;
    void requestPasswordReset(email, lang === 'FR' ? 'fr' : 'en');
  };

  return (
    <AdminAuthShell>
      <div className={styles.topBar}>
          <LangToggle value={lang} onChange={setLang} />
        </div>

        <div className={styles.formColumn}>
          <header className={styles.header}>
            <span className={styles.eyebrow}>
              {sentEmail ? t('forgotPassword.eyebrow') : t('forgotPassword.eyebrow')}
            </span>
            <h1 className={styles.title}>
              {sentEmail ? t('forgotPassword.successHeadline') : t('forgotPassword.headline')}
            </h1>
          </header>

          <Card>
            {sentEmail ? (
              // ─── Success panel (after submit) ──────────────────────
              <div className={styles.form}>
                <p className={styles.body}>
                  {tt('forgotPassword.successBodyTemplate', { email: sentEmail })}
                </p>
                <Link to="/login" className={styles.inlineLink}>
                  {t('forgotPassword.backLink')}
                </Link>
              </div>
            ) : (
              // ─── Email entry form ──────────────────────────────────
              <form onSubmit={onSubmit} className={styles.form} noValidate>
                <p className={styles.body}>{t('forgotPassword.body')}</p>

                {error && (
                  <Alert severity="danger" onDismiss={clearError}>
                    {error.code === 'RATE_LIMITED'
                      ? t('forgotPassword.errorRateLimited')
                      : t('forgotPassword.errorGeneric')}
                  </Alert>
                )}

                <TextInput
                  label={t('forgotPassword.emailLabel')}
                  type="email"
                  name="email"
                  autoComplete="username"
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  disabled={submitting}
                  required
                  autoFocus
                />

                <Button type="submit" kind="brand" fullWidth loading={submitting}>
                  {t('forgotPassword.submitButton')}
                </Button>

                <Link to="/login" className={styles.inlineLink}>
                  {t('forgotPassword.backLink')}
                </Link>
              </form>
            )}
          </Card>
        </div>
    </AdminAuthShell>
  );
}
